import { useAuth } from '../../context/AuthContext';
import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import Header from '../../components/common/Header';
import DoctorBottomNav from '../../components/doctor/DoctorBottomNav';
import { InlineLoader } from '../../components/common/Loader';
import { getEarningsSummary, getEarningsTransactions, getEarningsChart } from '../../services/doctorService';
import { formatCurrency, formatDate } from '../../utils/helpers';

const PERIODS = [
  { key: 'today',   label: 'Today' },
  { key: 'weekly',  label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' },
];


// ── Earnings Bar Chart — clean, responsive, with tooltip ──────────────────
function EarningsChart({ data, period }) {
  const [tooltip, setTooltip] = React.useState(null);

  if (!data || data.length === 0) return (
    <div className="card-p flex flex-col items-center justify-center py-8 gap-2">
      <i className="fa-solid fa-chart-column text-4xl text-slate-200" />
      <p className="text-sm font-medium text-text-muted">No chart data for this period</p>
      <p className="text-xs text-text-muted">Complete more appointments to see earnings</p>
    </div>
  );

  const maxVal = Math.max(...data.map(d => d.amount || 0), 1);
  const totalShown = data.reduce((s, d) => s + (d.amount || 0), 0);

  const formatLabel = (dateStr) => {
    try {
      const d = new Date(dateStr + 'T00:00:00');
      if (period === 'today') return d.toLocaleTimeString('en', { hour: '2-digit', hour12: true });
      if (period === 'weekly') return d.toLocaleDateString('en', { weekday: 'short' });
      return d.toLocaleDateString('en', { day: 'numeric', month: 'short' });
    } catch { return dateStr; }
  };

  // Y-axis ticks
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(f => Math.round(maxVal * f));

  return (
    <div className="card-p">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm font-bold text-text-primary">Earnings Chart</p>
          <p className="text-xs text-text-muted mt-0.5">
            Total: <span className="text-primary font-semibold">₹{Math.round(totalShown).toLocaleString('en-IN')}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-sm bg-primary" />
          <span className="text-xs text-text-muted">{data.length} entries</span>
        </div>
      </div>

      <div className="relative" style={{ height: 160 }}>
        {/* Y-axis labels */}
        <div className="absolute left-0 top-0 bottom-8 flex flex-col justify-between text-right" style={{ width: 44 }}>
          {[...yTicks].reverse().map((v, i) => (
            <span key={i} className="text-[9px] text-text-muted pr-1.5">
              {v >= 1000 ? `₹${(v/1000).toFixed(1)}k` : `₹${v}`}
            </span>
          ))}
        </div>

        {/* Grid lines + bars */}
        <div className="absolute left-12 right-0 top-0 bottom-8">
          {/* Grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map((f, i) => (
            <div key={i} className="absolute left-0 right-0 border-t border-dashed border-slate-100"
              style={{ bottom: `${f * 100}%` }} />
          ))}

          {/* Bars */}
          <div className="absolute inset-0 flex items-end gap-0.5 overflow-x-auto scrollbar-hide pb-0">
            {data.map((d, i) => {
              const pct = Math.max(2, Math.round(((d.amount || 0) / maxVal) * 100));
              const isActive = tooltip?.i === i;
              return (
                <div key={i} className="flex flex-col items-center justify-end flex-1 min-w-[20px] h-full relative"
                  onMouseEnter={() => setTooltip({ i, d })}
                  onMouseLeave={() => setTooltip(null)}
                  onTouchStart={() => setTooltip({ i, d })}
                  onTouchEnd={() => setTimeout(() => setTooltip(null), 1200)}>
                  {/* Tooltip */}
                  {isActive && (
                    <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 z-10
                      bg-gray-900 text-white rounded-lg px-2 py-1 text-[10px] whitespace-nowrap shadow-lg">
                      <div className="font-bold">₹{Math.round(d.amount || 0).toLocaleString('en-IN')}</div>
                      <div className="text-white/70">{formatLabel(d._id)}</div>
                      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                    </div>
                  )}
                  <div className="w-full rounded-t-md transition-all duration-200"
                    style={{
                      height: `${pct}%`,
                      background: isActive
                        ? 'linear-gradient(180deg,#0f766e,#0D9488)'
                        : 'linear-gradient(180deg,#0D9488,#5eead4)',
                      transform: isActive ? 'scaleX(1.1)' : 'scaleX(1)',
                    }} />
                </div>
              );
            })}
          </div>
        </div>

        {/* X-axis labels */}
        <div className="absolute left-12 right-0 bottom-0 flex gap-0.5 overflow-x-hidden" style={{ height: 28 }}>
          {data.map((d, i) => (
            <div key={i} className="flex-1 min-w-[20px] text-center">
              <span className="text-[9px] text-text-muted">{formatLabel(d._id)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Summary row */}
      <div className="mt-3 pt-3 border-t border-border flex justify-between text-xs text-text-muted">
        <span>Min: <b className="text-text-primary">₹{Math.round(Math.min(...data.map(d=>d.amount||0)))}</b></span>
        <span>Avg: <b className="text-text-primary">₹{Math.round(totalShown / data.length)}</b></span>
        <span>Max: <b className="text-text-primary">₹{Math.round(maxVal)}</b></span>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, icon, color, bg }) {
  return (
    <div className="card-p flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center flex-shrink-0`}>
        <i className={`fa-solid ${icon} ${color} text-base`} />
      </div>
      <div>
        <p className="text-lg font-bold text-text-primary font-heading">{value}</p>
        <p className="text-xs text-text-muted">{label}</p>
      </div>
    </div>
  );
}

export default function DoctorEarnings() {
  const { role, user } = useAuth();


  const [period, setPeriod]     = useState('today');
  const [summary, setSummary]   = useState(null);
  const [transactions, setTxns] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [page, setPage]         = useState(1);
  const [total, setTotal]       = useState(0);
  const [chartData, setChartData] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t, c] = await Promise.all([
        getEarningsSummary({ period }),
        getEarningsTransactions({ period, page: 1, limit: 20 }),
        getEarningsChart({ period }),
      ]);
      setSummary(s.summary);
      setTxns(t.payments || []);
      setTotal(t.total || 0);
      setChartData(c.chartData || []);
      setPage(1);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const loadMore = async () => {
    try {
      const res = await getEarningsTransactions({ period, page: page + 1, limit: 20 });
      setTxns((p) => [...p, ...(res.payments || [])]);
      setPage((p) => p + 1);
    } catch {}
  };

  const progressPct = summary?.target > 0
    ? Math.min(100, Math.round((summary.totalEarned / summary.target) * 100))
    : 0;


  // ── Staff permission guard (must be AFTER all hooks) ──────────────────
  if (role === 'staff' && !user?.permissions?.['earnings']) {
    return (
      <div className="page-container flex flex-col items-center justify-center py-20 gap-4 text-center px-8">
        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
          <i className="fa-solid fa-lock text-red-500 text-3xl" />
        </div>
        <h2 className="text-lg font-bold text-text-primary">Access Restricted</h2>
        <p className="text-sm text-text-muted leading-relaxed">
          You don&apos;t have permission to access this section.<br />
          Contact your doctor to enable it.
        </p>
        <button onClick={() => window.history.back()}
          className="px-6 py-2.5 bg-primary text-white rounded-xl text-sm font-bold">
          Go Back
        </button>
      </div>
    );
  }


  return (
    <div className="page-container">
      <Header title="Earnings" showBack />

      {/* Period selector */}
      <div className="flex bg-white border-b border-border">
        {PERIODS.map((p) => (
          <button key={p.key} onClick={() => setPeriod(p.key)}
            className={`flex-1 py-3 text-sm font-semibold border-b-2 transition-all ${
              period === p.key ? 'border-primary text-primary' : 'border-transparent text-text-muted'
            }`}>
            {p.label}
          </button>
        ))}
      </div>

      <div className="px-4 py-4 space-y-4">
        {loading ? <InlineLoader text="Loading earnings..." /> : (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-2 gap-2">
              <SummaryCard label="Total Earned"    value={formatCurrency(summary?.totalEarned || 0)}    icon="fa-circle-check"    color="text-success"  bg="bg-green-50" />
              <SummaryCard label="Pending Release" value={formatCurrency(summary?.pendingRelease || 0)} icon="fa-clock"           color="text-accent"   bg="bg-amber-50" />
              <SummaryCard label="Released"        value={formatCurrency(summary?.released || 0)}        icon="fa-bank"            color="text-info"     bg="bg-blue-50" />
              <SummaryCard label="Daily Target"    value={formatCurrency(summary?.target || 0)}          icon="fa-bullseye"        color="text-primary"  bg="bg-primary-light" />
            </div>

            {/* ── Earnings Breakdown — Offline / Online Clinic / Video ── */}
            {(summary?.totalEarned || 0) > 0 && (
              <div className="space-y-3">
                <p className="text-xs font-bold text-text-muted uppercase tracking-wide px-1">Earnings Breakdown</p>

                {/* 3-column grid */}
                <div className="grid grid-cols-3 gap-2">
                  {/* Walk-in / Offline */}
                  <div className="bg-orange-50 border border-orange-200 rounded-2xl p-3">
                    <div className="w-7 h-7 bg-orange-100 rounded-lg flex items-center justify-center mb-2">
                      <i className="fa-solid fa-person-walking text-orange-600 text-xs" />
                    </div>
                    <p className="text-[10px] font-semibold text-orange-700">Walk-in</p>
                    <p className="text-sm font-black text-orange-800 mt-0.5">{formatCurrency(summary?.offlineEarned || 0)}</p>
                    <p className="text-[9px] text-orange-500 mt-0.5">{summary?.offlineCount || 0} visits</p>
                  </div>
                  {/* Online Clinic Visit */}
                  <div className="bg-primary-light border border-primary/20 rounded-2xl p-3">
                    <div className="w-7 h-7 bg-white rounded-lg flex items-center justify-center mb-2">
                      <i className="fa-solid fa-hospital text-primary text-xs" />
                    </div>
                    <p className="text-[10px] font-semibold text-primary">Clinic Visit</p>
                    <p className="text-sm font-black text-primary mt-0.5">{formatCurrency(summary?.clinicEarned || 0)}</p>
                    <p className="text-[9px] text-primary/60 mt-0.5">{summary?.clinicCount || 0} visits</p>
                  </div>
                  {/* Video */}
                  <div className="bg-blue-50 border border-blue-200 rounded-2xl p-3">
                    <div className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center mb-2">
                      <i className="fa-solid fa-video text-blue-600 text-xs" />
                    </div>
                    <p className="text-[10px] font-semibold text-blue-700">Video Call</p>
                    <p className="text-sm font-black text-blue-800 mt-0.5">{formatCurrency(summary?.videoEarned || 0)}</p>
                    <p className="text-[9px] text-blue-500 mt-0.5">{summary?.videoCount || 0} calls</p>
                  </div>
                </div>

                {/* Progress bar */}
                {(summary?.totalEarned || 0) > 0 && (() => {
                  const t = summary.totalEarned;
                  const offPct  = Math.round(((summary?.offlineEarned || 0) / t) * 100);
                  const clnPct  = Math.round(((summary?.clinicEarned  || 0) / t) * 100);
                  const vidPct  = 100 - offPct - clnPct;
                  return (
                    <div>
                      <div className="flex justify-between text-[9px] text-text-muted mb-1">
                        <span className="text-orange-600 font-semibold">Walk-in {offPct}%</span>
                        <span className="text-primary font-semibold">Clinic {clnPct}%</span>
                        <span className="text-blue-600 font-semibold">Video {vidPct}%</span>
                      </div>
                      <div className="h-2 bg-border rounded-full overflow-hidden flex">
                        <div className="bg-orange-400 h-full" style={{ width: `${offPct}%` }} />
                        <div className="bg-primary h-full"   style={{ width: `${clnPct}%` }} />
                        <div className="bg-blue-500 h-full"  style={{ width: `${Math.max(vidPct,0)}%` }} />
                      </div>
                    </div>
                  );
                })()}

                {/* Date-wise table */}
                {summary?.byDate?.length > 0 && (
                  <div className="bg-white border border-border rounded-2xl overflow-hidden">
                    <div className="px-3 py-2.5 border-b border-border bg-surface">
                      <p className="text-xs font-bold text-text-primary">Date-wise Earnings</p>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-surface border-b border-border">
                            <th className="text-left px-3 py-2 text-text-muted font-semibold">Date</th>
                            <th className="text-right px-2 py-2 text-orange-600 font-semibold">Walk-in</th>
                            <th className="text-right px-2 py-2 text-primary font-semibold">Clinic</th>
                            <th className="text-right px-2 py-2 text-blue-600 font-semibold">Video</th>
                            <th className="text-right px-3 py-2 text-success font-semibold">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {summary.byDate.map((row, i) => (
                            <tr key={i} className="border-b border-border last:border-0 hover:bg-surface">
                              <td className="px-3 py-2 font-medium text-text-primary">{row.date}</td>
                              <td className="px-2 py-2 text-right text-orange-700">{row.offline > 0 ? formatCurrency(row.offline) : '—'}</td>
                              <td className="px-2 py-2 text-right text-primary">{row.clinic  > 0 ? formatCurrency(row.clinic)  : '—'}</td>
                              <td className="px-2 py-2 text-right text-blue-600">{row.video   > 0 ? formatCurrency(row.video)   : '—'}</td>
                              <td className="px-3 py-2 text-right font-bold text-success">{formatCurrency(row.total)}</td>
                            </tr>
                          ))}
                        </tbody>
                        {/* Totals row */}
                        <tfoot>
                          <tr className="bg-green-50 border-t-2 border-primary/20">
                            <td className="px-3 py-2 font-bold text-text-primary">Total</td>
                            <td className="px-2 py-2 text-right font-bold text-orange-700">{formatCurrency(summary?.offlineEarned || 0)}</td>
                            <td className="px-2 py-2 text-right font-bold text-primary">{formatCurrency(summary?.clinicEarned || 0)}</td>
                            <td className="px-2 py-2 text-right font-bold text-blue-600">{formatCurrency(summary?.videoEarned || 0)}</td>
                            <td className="px-3 py-2 text-right font-bold text-success">{formatCurrency(summary?.totalEarned || 0)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Earnings Chart */}
            <EarningsChart data={chartData} period={period} />

            {/* Progress toward daily target */}
            {summary?.target > 0 && (
              <div className="card-p">
                <div className="flex justify-between text-xs mb-2">
                  <span className="text-text-secondary font-medium">Daily Target Progress</span>
                  <span className={`font-bold ${progressPct >= 100 ? 'text-success' : 'text-primary'}`}>{progressPct}%</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${progressPct >= 100 ? 'bg-success' : 'bg-primary'}`}
                    style={{ width: `${progressPct}%` }} />
                </div>
                <p className="text-[10px] text-text-muted mt-1.5">
                  {formatCurrency(summary.totalEarned)} of {formatCurrency(summary.target)} earned today
                </p>
              </div>
            )}

            {/* Transaction list */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-text-primary">Transactions</p>
                <span className="text-xs text-text-muted">{total} total</span>
              </div>

              {transactions.length === 0 ? (
                <div className="text-center py-10">
                  <i className="fa-solid fa-receipt text-3xl text-text-muted mb-2 block" />
                  <p className="text-sm text-text-muted">No transactions yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {transactions.map((txn) => {
                    const isCancelled  = txn.status === 'refunded';
                    const cancelFee    = txn.cancellationCharge || txn.appointmentId?.cancellationCharge || 0;
                    const cancelReleased = txn.cancellationReleased || false;
                    const displayAmt   = isCancelled
                      ? cancelFee
                      : (txn.doctorFee || 0) - (txn.discountApplied || 0);

                    return (
                    <div key={txn._id} className={`card-p flex items-center gap-3 ${isCancelled ? 'opacity-90' : ''}`}>
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isCancelled      ? 'bg-red-50'    :
                        txn.status === 'released' ? 'bg-green-50' : 'bg-amber-50'
                      }`}>
                        <i className={`fa-solid text-sm ${
                          isCancelled      ? 'fa-ban text-danger'         :
                          txn.status === 'released' ? 'fa-indian-rupee-sign text-success' : 'fa-indian-rupee-sign text-accent'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-text-primary truncate">
                          {txn.patientId?.name || 'Patient'}
                          {isCancelled && <span className="ml-1.5 text-[10px] font-bold text-red-500 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded-full">Cancelled</span>}
                        </p>
                        <p className="text-xs text-text-muted">
                          Token #{txn.appointmentId?.tokenNumber} ·
                          {txn.appointmentId?.appointmentDate ? ' ' + formatDate(txn.appointmentId.appointmentDate) : ''}
                        </p>
                        {isCancelled && (
                          <p className="text-[11px] mt-0.5">
                            {cancelFee > 0 ? (
                              <span className={`font-semibold ${cancelReleased ? 'text-success' : 'text-accent'}`}>
                                <i className={`fa-solid ${cancelReleased ? 'fa-circle-check' : 'fa-clock'} mr-1 text-[9px]`} />
                                {cancelReleased
                                  ? `Cancellation fee ₹${cancelFee} received`
                                  : `Cancellation fee ₹${cancelFee} — pending admin approval`
                                }
                              </span>
                            ) : (
                              <span className="text-text-muted">No cancellation fee</span>
                            )}
                          </p>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className={`text-sm font-bold ${isCancelled && cancelFee === 0 ? 'text-text-muted' : 'text-text-primary'}`}>
                          {displayAmt > 0 ? formatCurrency(displayAmt) : '—'}
                        </p>
                        <span className={`text-[10px] font-medium ${
                          isCancelled && cancelReleased ? 'text-success' :
                          isCancelled ? 'text-danger' :
                          txn.status === 'released' ? 'text-success' : 'text-accent'
                        }`}>
                          {isCancelled && cancelReleased ? 'Cancel Fee Released' :
                           isCancelled ? 'Cancelled' :
                           txn.status === 'released' ? 'Released' : 'Pending Release'}
                        </span>
                      </div>
                    </div>
                  )})}

                  {transactions.length < total && (
                    <button onClick={loadMore}
                      className="w-full py-3 text-sm text-primary font-semibold border border-primary rounded-btn">
                      Load More
                    </button>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <DoctorBottomNav />
    </div>
  );
}
